import {
  FC,
  HTMLAttributes,
  PropsWithChildren,
  useEffect,
  useRef,
} from 'react';
import { StyledDrawer } from './Drawer.styles';
import { useTheme } from 'styled-components';
import useMediaQuery from '../../utils/useMediaQuery/useMediaQuery';

export interface DrawerProps
  extends PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
  /** whether the drawer is visible or not */
  visible: boolean;
  /** callback to be called when the drawer is closed, should adjust the visible property */
  onClose: () => void;
  /** the background variant of the drawer */
  bgVariant?: 'primary' | 'secondary';
  /** whether the drawer should always be visible on desktop. Ignores the visible prop on desktop if true */
  alwaysShowOnDesktop?: boolean;
}

/**
 * Toggleable drawer component that slides in from the side. Has two background
 * variants
 */
export const Drawer: FC<DrawerProps> = ({
  children,
  visible,
  onClose,
  alwaysShowOnDesktop,
  bgVariant = 'primary',
  ...otherProps
}) => {
  const theme = useTheme();
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<Element | null>(null);
  const isDesktop = useMediaQuery(
    `(min-width: ${theme.measures.breakpoints.tabletLg})`,
  );
  const isVisible = visible || (isDesktop && alwaysShowOnDesktop);
  useEffect(() => {
    if (isVisible) {
      document.body.addEventListener('click', onClose);
      // enable focus within the drawer (React/TS don't like the inert attr yet)
      drawerRef.current?.removeAttribute('inert');
      // Store a reference to the previously focused element and set the focus
      // to the first element with `autofocus` within the children or the close
      // button.
      previouslyFocusedRef.current = document.activeElement;
      const focusTarget = drawerRef.current?.querySelector(
        'button,a,[href],input,select,textarea,[autofocus],[tabindex]:not([tabindex="-1"])',
      );
      (focusTarget as HTMLFormElement)?.focus();
    } else {
      document.body.removeEventListener('click', onClose);
      // prevent focus within the drawer
      drawerRef.current?.setAttribute('inert', '');
      // re-focus previously-focused element
      (previouslyFocusedRef.current as HTMLFormElement)?.focus();
      previouslyFocusedRef.current = null;
    }
    return () => {
      document.body.removeEventListener('click', onClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);
  return (
    <StyledDrawer
      $alwaysShowOnDesktop={alwaysShowOnDesktop}
      $bgVariant={bgVariant}
      $visible={!!isVisible}
      onClick={(e) => e.stopPropagation()}
      ref={drawerRef}
      {...otherProps}
    >
      {children}
    </StyledDrawer>
  );
};

export default Drawer;
